import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Avatar, Chip } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import { keyframes } from '@mui/system';
import { supabase } from '../services/supabase';
import { LiveGame, LiveGamePlayer, Player, RoleType, WinnerType } from '../types/models';

// Define orbit animation - container rotates to move card positions in a circle
const orbitAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

// Counter-rotation to keep cards upright while orbiting
const counterRotateAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(-360deg);
  }
`;

// Define entry animation - cards gather from bottom to orbit positions
const gatherAnimation = keyframes`
  0% {
    opacity: 0;
    transform: translateY(350px) scale(0.5);
  }
  60% {
    opacity: 1;
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

// Define exit animation - winner cards fly up from bottom row toward center
const flyToCenterAnimation = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateY(-400px) scale(0.6);
  }
`;

// A minimal player card specifically designed for OBS overlay
const PlayerOverlayCard: React.FC<{
  player: LiveGamePlayer;
  playerDetails: Player;
  seatNumber: number;
  isAnimating: boolean;
  winner: WinnerType | null | undefined;
  showFinalState: boolean;
}> = ({ player, playerDetails, seatNumber, isAnimating, winner, showFinalState }) => {
  const isKilled = player.is_killed;
  const role = player.role as RoleType;
  
  // Get role color - same as in the admin component
  let roleColor = '#bdbdbd'; // Default gray
  let textColor = '#ffffff'; // Default white text
  
  if (role) {
    switch (role) {
      case 'CITIZEN':
        roleColor = '#e57373'; // Light Red
        textColor = '#ffffff';
        break;
      case 'SHERIFF':
        roleColor = '#c62828'; // Dark Red
        textColor = '#ffffff';
        break;
      case 'MAFIA':
        roleColor = '#424242'; // Dark Gray
        textColor = '#ffffff';
        break;
      case 'DON':
        roleColor = '#212121'; // Almost Black
        textColor = '#ffffff';
        break;
    }
  }

  // Get role name for display
  const getRoleDisplay = (role?: RoleType | null) => {
    if (!role) return '';
    
    switch (role) {
      case 'CITIZEN': return 'Citizen';
      case 'SHERIFF': return 'Sheriff';
      case 'MAFIA': return 'Mafia';
      case 'DON': return 'Don';
      default: return role;
    }
  };
  
  // Check if this player is part of the winning team
  const isWinner = (winner === 'CIVILIANS' && (role === 'CITIZEN' || role === 'SHERIFF')) ||
                   (winner === 'MAFIA' && (role === 'MAFIA' || role === 'DON'));
  
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        width: '95px',
        // Apply different styles based on game state
        ...(showFinalState ? {
          // Final state - small cards at bottom
          transform: isWinner ? 'scale(0.9)' : 'scale(0.5)',
          opacity: isKilled ? 0.4 : 0.7,
          transition: 'all 0.7s ease-in-out',
        } : {
          // Normal state
          opacity: isKilled ? 0.5 : 1,
        })
      }}
    >
      {/* Player Number Badge */}
      <Box
        sx={{
          bgcolor: roleColor,
          color: textColor,
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'bold',
          fontSize: '18px',
          position: 'absolute',
          top: -10,
          left: '-1vw',
          border: '3px solid #fff',
          zIndex: 3,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
        }}
      >
        {seatNumber}
      </Box>

      {/* Card container with avatar and name */}
      <Box
        sx={{
          position: 'relative',
          bgcolor: roleColor,
          borderRadius: '12px',
          border: '3px solid #fff',
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        }}
      >
        {/* Player Avatar */}
        <Avatar
          sx={{
            width: '8.5vw',
            height: 150,
            bgcolor: roleColor,
            color: textColor,
            borderRadius: 0,
            opacity: isKilled ? 0.7 : 1,
          }}
          src={playerDetails?.profile_picture_url || undefined}
        >
          {!playerDetails.profile_picture_url &&
            (playerDetails.nickname
              ? playerDetails.nickname.substring(0, 2).toUpperCase()
              : <PersonIcon sx={{ fontSize: 48 }} />)
          }
        </Avatar>

        {/* Player Name - integrated into card */}
        <Box
          sx={{
            bgcolor: 'rgba(0, 0, 0, 0.6)',
            padding: '8px 4px',
          }}
        >
          <Typography
            sx={{
              color: '#ffffff',
              fontWeight: 'bold',
              textAlign: 'center',
              fontSize: '0.95rem',
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.8)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {playerDetails.nickname}
          </Typography>
        </Box>

        {/* X overlay for killed players */}
        {isKilled && (
          <>
            <Box sx={{
              position: 'absolute',
              top: '40%',
              left: '50%',
              width: '120%',
              height: '5px',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              transform: 'translate(-50%, -50%) rotate(45deg)',
              zIndex: 1
            }} />
            <Box sx={{
              position: 'absolute',
              top: '40%',
              left: '50%',
              width: '120%',
              height: '5px',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              transform: 'translate(-50%, -50%) rotate(-45deg)',
              zIndex: 1
            }} />
          </>
        )}

        {/* Role icon badges */}
        {role === 'SHERIFF' && (
          <Avatar
            sx={{
              position: 'absolute',
              bottom: 40,
              right: -6,
              width: 34,
              height: 34,
              bgcolor: '#c62828',
              color: 'white',
              border: '2px solid white',
              zIndex: 2,
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.4)',
            }}
          >
            <LocalPoliceIcon sx={{ fontSize: 20 }} />
          </Avatar>
        )}

        {role === 'DON' && (
          <Avatar
            sx={{
              position: 'absolute',
              bottom: 40,
              right: -6,
              width: 34,
              height: 34,
              bgcolor: '#212121',
              color: 'white',
              border: '2px solid white',
              zIndex: 2,
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.4)',
            }}
          >
            <TheaterComedyIcon sx={{ fontSize: 20 }} />
          </Avatar>
        )}
      </Box>
    </Box>
  );
};

const LiveGameOverlay: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const [loading, setLoading] = useState(true);
  const [gameData, setGameData] = useState<LiveGame | null>(null);
  const [gamePlayers, setGamePlayers] = useState<LiveGamePlayer[]>([]);
  const [allPlayers, setAllPlayers] = useState<Record<string, Player>>({});
  const [isAnimating, setIsAnimating] = useState(false);
  const [showFinalState, setShowFinalState] = useState(false);
  const prevStatus = useRef<string | null>(null);
  const prevWinner = useRef<WinnerType | null | undefined>(null);
  
  // Set transparent background on body for OBS overlay
  useEffect(() => {
    // Store original styles
    const originalBodyBackground = document.body.style.background;
    const originalBodyBgColor = document.body.style.backgroundColor;
    const originalHtmlBackground = document.documentElement.style.backgroundColor;

    // Override all background styles
    document.body.style.background = 'transparent';
    document.body.style.backgroundColor = 'transparent';
    document.documentElement.style.backgroundColor = 'transparent';

    // Add a style tag to override CSS rules including ::before pseudo-element
    const styleTag = document.createElement('style');
    styleTag.id = 'overlay-transparent-style';
    styleTag.innerHTML = `
      html, body {
        background: transparent !important;
        background-color: transparent !important;
      }
      body::before {
        display: none !important;
      }
    `;
    document.head.appendChild(styleTag);

    return () => {
      document.body.style.background = originalBodyBackground;
      document.body.style.backgroundColor = originalBodyBgColor;
      document.documentElement.style.backgroundColor = originalHtmlBackground;
      const existingStyle = document.getElementById('overlay-transparent-style');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (gameId) {
      // Initial fetch
      fetchGameData(gameId);
      
      // Set up polling for updates every 3 seconds
      const pollingInterval = setInterval(() => {
        fetchGameData(gameId);
      }, 3000);
      
      // Set up realtime subscriptions as backup
      const liveGameSubscription = supabase
        .channel('live-game-changes')
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'live_games',
            filter: `id=eq.${gameId}`
          },
          (payload) => {
            console.log('Live game update received:', payload);
            // Always update with the new data from the database
            const newData = payload.new as LiveGame;
            setGameData(newData);
          }
        )
        .on(
          'broadcast',
          { event: 'overlay_text_changed' },
          (payload) => {
            console.log('Broadcast received:', payload);
            if (payload.payload && payload.payload.gameId === gameId) {
              // Force refresh the entire game data when overlay text changes
              fetchGameData(gameId);
            }
          }
        )
        .subscribe();
        
      const liveGamePlayersSubscription = supabase
        .channel('live-game-players-changes')
        .on(
          'postgres_changes',
          {
            event: '*', // UPDATE, INSERT, DELETE
            schema: 'public',
            table: 'live_game_players',
            filter: `live_game_id=eq.${gameId}`
          },
          () => {
            // When we get any change, refetch all players to keep it simple
            fetchPlayersForGame(gameId);
          }
        )
        .subscribe();
        
      // Cleanup subscriptions and interval
      return () => {
        clearInterval(pollingInterval);
        supabase.removeChannel(liveGameSubscription);
        supabase.removeChannel(liveGamePlayersSubscription);
      };
    }
  }, [gameId]);
  
  const fetchGameData = async (id: string) => {
    try {
      // Only set loading on the initial fetch
      if (loading === false && !gameData) {
        setLoading(true);
      }
      
      // Fetch game data
      const { data: fetchedGameData, error: gameError } = await supabase
        .from('live_games')
        .select(`
          *,
          league:league_id (
            id,
            name
          ),
          judge:judge_id (
            id,
            nickname,
            full_name
          )
        `)
        .eq('id', id)
        .single();

      if (gameError) throw gameError;
      
      // Update state if data actually changed, including overlay text
      setGameData(prev => {
        if (!prev || 
            prev.current_day !== fetchedGameData.current_day || 
            prev.status !== fetchedGameData.status || 
            prev.winner !== fetchedGameData.winner ||
            prev.overlay_text !== fetchedGameData.overlay_text) {
              
          // Check if the game just completed - trigger animation
          if (fetchedGameData.status === 'COMPLETED' && 
              prev?.status !== 'COMPLETED' && 
              fetchedGameData.winner) {
            prevStatus.current = fetchedGameData.status;
            prevWinner.current = fetchedGameData.winner as WinnerType;
            
            // Start the animation - it will continue until game is reset
            setIsAnimating(true);
            setShowFinalState(false);
          }
          
          return fetchedGameData as LiveGame;
        }
        return prev;
      });
      
      // Fetch players for this game
      await fetchPlayersForGame(id);
    } catch (error: any) {
      console.error('Error fetching game data:', error.message);
    } finally {
      if (!gameData) {
        setLoading(false);
      }
    }
  };
  
  const fetchPlayersForGame = async (id: string) => {
    try {
      // Fetch players in this game
      const { data: playersData, error: playersError } = await supabase
        .from('live_game_players')
        .select('*')
        .eq('live_game_id', id)
        .order('seat_number');
        
      if (playersError) throw playersError;
      
      if (!playersData || playersData.length === 0) {
        return;
      }
      
      // Check if we already have all the player details
      // If we already have them, and the data hasn't changed, don't update state to prevent rerender
      let hasChanges = false;
      
      // Compare with current players data
      if (gamePlayers.length !== playersData.length) {
        hasChanges = true;
      } else {
        // Check for changes in player data
        for (let i = 0; i < playersData.length; i++) {
          const newPlayer = playersData[i];
          const currentPlayer = gamePlayers.find(p => p.id === newPlayer.id);
          
          if (!currentPlayer ||
              currentPlayer.role !== newPlayer.role ||
              currentPlayer.is_killed !== newPlayer.is_killed ||
              currentPlayer.killed_day !== newPlayer.killed_day) {
            hasChanges = true;
            break;
          }
        }
      }
      
      // Only proceed with updates if changes detected
      if (hasChanges) {
        // Fetch all player details if needed
        const playerIds = playersData.map(p => p.player_id);
        const missingPlayerIds = playerIds.filter(id => !allPlayers[id]);
        
        if (missingPlayerIds.length > 0) {
          const { data: playerDetails, error: playerDetailsError } = await supabase
            .from('players')
            .select('*')
            .in('id', missingPlayerIds);
            
          if (playerDetailsError) throw playerDetailsError;
          
          // Create an updated lookup object for players
          const updatedPlayersLookup = { ...allPlayers };
          playerDetails?.forEach(player => {
            updatedPlayersLookup[player.id] = player;
          });
          
          setAllPlayers(updatedPlayersLookup);
        }
        
        // Update game players
        setGamePlayers(playersData as LiveGamePlayer[]);
      }
    } catch (error: any) {
      console.error('Error fetching players data:', error.message);
    }
  };
  
  // If still loading
  if (loading) {
    return null; // For OBS, we want a blank screen when loading
  }
  
  // If no game data was found after loading
  if (!gameData) {
    return (
      <Box 
        sx={{ 
          background: 'transparent',
          minHeight: '100vh',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: '8px',
            padding: '8px 16px',
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: '#ffffff',
              fontWeight: 'medium',
              textShadow: '0 0 5px rgba(0, 0, 0, 0.8)',
            }}
          >
            Game not found
          </Typography>
        </Box>
      </Box>
    );
  }
  
  return (
    <Box 
      sx={{ 
        // No background for OBS overlay
        background: 'transparent',
        minHeight: '100vh',
        overflow: 'hidden',
        position: 'relative',
        // No padding on main container to allow full screen usage
      }}
    >
      {/* Game info boxes in top left */}
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          left: 20,
          display: 'flex',
          gap: 2,
        }}
      >
        {/* Game Number Box */}
        <Box
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: '8px',
            padding: '8px 12px',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: '#ffffff',
              fontWeight: 'bold',
              textShadow: '0 0 5px rgba(0, 0, 0, 0.8)',
            }}
          >
            Game: {gameData?.number_in_session || 0}
          </Typography>
        </Box>

        {/* Round Box */}
        {gameData?.current_day !== undefined && (
          <Box
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              borderRadius: '8px',
              padding: '8px 12px',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#ffffff',
                fontWeight: 'bold',
                textShadow: '0 0 5px rgba(0, 0, 0, 0.8)',
              }}
            >
              Round: {Math.floor(gameData.current_day / 2)}
            </Typography>
          </Box>
        )}

        {/* Day/Night Icon Box */}
        {gameData?.current_day !== undefined && (
          <Box
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              borderRadius: '8px',
              padding: '8px 12px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {gameData.current_day % 2 === 0 ? (
              // Day icon - sun
              <Typography
                component="span"
                variant="h6"
                sx={{
                  color: '#FFD700', // Gold color for sun
                  fontWeight: 'bold',
                  textShadow: '0 0 5px rgba(0, 0, 0, 0.8)',
                }}
              >
                ☀️
              </Typography>
            ) : (
              // Night icon - moon
              <Typography
                component="span"
                variant="h6" 
                sx={{
                  color: '#E0E0E0', // Light silver for moon
                  fontWeight: 'bold',
                  textShadow: '0 0 5px rgba(0, 0, 0, 0.8)',
                }}
              >
                🌙
              </Typography>
            )}
          </Box>
        )}
      </Box>
      
      {/* Custom Text on Top Right */}
      {gameData?.overlay_text && (
        <Box
          sx={{
            position: 'absolute',
            top: 20,
            right: 20,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: '8px',
            padding: '8px 12px',
            maxWidth: '50%'
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: '#ffffff',
              fontWeight: 'bold',
              textShadow: '0 0 5px rgba(0, 0, 0, 0.8)',
              wordBreak: 'break-word'
            }}
          >
            {gameData.overlay_text}
          </Typography>
        </Box>
      )}
      
      {/* Sheriff Checks Box */}
      {gamePlayers.some(player => player.sheriff_checked) && (
        <Box
          sx={{
            position: 'absolute',
            top: 85,
            left: 20,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: '8px',
            padding: '8px 12px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalPoliceIcon sx={{ color: '#c62828' }} /> Sheriff
            <Typography
              component="span"
              sx={{
                color: '#ffffff',
                fontWeight: 'bold',
              }}
            >
              →
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {gamePlayers
                .filter(player => player.sheriff_checked)
                .sort((a, b) => a.seat_number - b.seat_number)
                .map(player => (
                  <Chip 
                    key={player.id}
                    label={player.seat_number}
                    size="small"
                    sx={{ 
                      bgcolor: '#c62828', 
                      color: 'white',
                      fontWeight: 'bold',
                      minWidth: '30px'
                    }}
                  />
                ))
              }
            </Box>
          </Box>
        </Box>
      )}
      
      {/* Don Checks Box */}
      {gamePlayers.some(player => player.don_checked) && (
        <Box
          sx={{
            position: 'absolute',
            top: 140,
            left: 20,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: '8px',
            padding: '8px 12px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TheaterComedyIcon sx={{ color: '#fff' }} /> Don
            <Typography
              component="span"
              sx={{
                color: '#ffffff',
                fontWeight: 'bold',
              }}
            >
              →
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {gamePlayers
                .filter(player => player.don_checked)
                .sort((a, b) => a.seat_number - b.seat_number)
                .map(player => (
                  <Chip 
                    key={player.id}
                    label={player.seat_number}
                    size="small"
                    sx={{ 
                      bgcolor: '#212121', 
                      color: 'white',
                      fontWeight: 'bold',
                      minWidth: '30px'
                    }}
                  />
                ))
              }
            </Box>
          </Box>
        </Box>
      )}
      
      {/* Winner banner and orbit animation - visible during animation and final state */}
      {(isAnimating || showFinalState) && gameData?.winner && (
        <>
          {/* Central winner banner */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: gameData.winner === 'CIVILIANS' ? 'rgba(198, 40, 40, 0.95)' : 'rgba(33, 33, 33, 0.95)',
              padding: '20px 50px',
              borderRadius: '16px',
              boxShadow: '0 8px 40px rgba(0, 0, 0, 0.6)',
              zIndex: 25,
              animation: isAnimating ? 'pulse 2s ease-in-out infinite' : 'none',
              '@keyframes pulse': {
                '0%, 100%': {
                  transform: 'translate(-50%, -50%) scale(1)',
                },
                '50%': {
                  transform: 'translate(-50%, -50%) scale(1.05)',
                }
              }
            }}
          >
            <Typography
              variant="h2"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                textAlign: 'center',
                textShadow: '0 2px 10px rgba(0, 0, 0, 0.7)',
                whiteSpace: 'nowrap',
              }}
            >
              {gameData.winner === 'CIVILIANS' ? 'Civilians Win!' : 'Mafia Wins!'}
            </Typography>
          </Box>

          {/* Orbit container for winning players during animation */}
          {isAnimating && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: 0,
                height: 0,
                zIndex: 20,
                // Delay gather to sync with fly-up animation, then continuous orbit
                animation: `${gatherAnimation} 1.3s ease-out 0.6s forwards, ${orbitAnimation} 20s linear 1.9s infinite`,
                opacity: 0, // Start invisible, gather animation will fade in
              }}
            >
              {gamePlayers
                .filter(p => {
                  const role = p.role as RoleType;
                  return (gameData.winner === 'CIVILIANS' && (role === 'CITIZEN' || role === 'SHERIFF')) ||
                         (gameData.winner === 'MAFIA' && (role === 'MAFIA' || role === 'DON'));
                })
                .map((player, index, arr) => {
                  const playerDetails = allPlayers[player.player_id];
                  if (!playerDetails) return null;

                  const angle = (360 / arr.length) * index;
                  const radius = 380; // Distance from center - increased for larger orbit

                  return (
                    <Box
                      key={`orbit-${player.id}`}
                      sx={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        // Position at angle from center
                        transform: `rotate(${angle}deg) translateY(-${radius}px)`,
                        marginLeft: '-47px',
                        marginTop: '-100px',
                      }}
                    >
                      {/* Counter-rotate to keep card upright */}
                      <Box
                        sx={{
                          // Cancel initial positioning rotation to start upright
                          // transform: `rotate(-${angle}deg)`,
                          // Then counter-rotate with orbit to stay upright
                          // animation: `${counterRotateAnimation} 20s linear 1.9s infinite`,
                        }}
                      >
                        <PlayerOverlayCard
                          player={player}
                          playerDetails={playerDetails}
                          seatNumber={player.seat_number}
                          isAnimating={true}
                          winner={gameData?.winner}
                          showFinalState={false}
                        />
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          )}
        </>
      )}

      {/* Player Cards Row at Bottom */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 25,
          left: 60,
          right: 60,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 0,
          zIndex: 1,
        }}
      >
        {gamePlayers.map((player) => {
          const playerDetails = allPlayers[player.player_id];
          if (!playerDetails) return null;

          const role = player.role as RoleType;
          const isWinner = (gameData?.winner === 'CIVILIANS' && (role === 'CITIZEN' || role === 'SHERIFF')) ||
                          (gameData?.winner === 'MAFIA' && (role === 'MAFIA' || role === 'DON'));

          // During animation, winners fly up toward center then disappear
          if (isAnimating && isWinner) {
            return (
              <Box
                key={player.id}
                sx={{
                  width: '95px',
                  animation: `${flyToCenterAnimation} 1.2s ease-in forwards`,
                }}
              >
                <PlayerOverlayCard
                  player={player}
                  playerDetails={playerDetails}
                  seatNumber={player.seat_number}
                  isAnimating={false}
                  winner={gameData?.winner}
                  showFinalState={showFinalState}
                />
              </Box>
            );
          }

          // During animation, fade out non-winners
          const animatingOpacity = isAnimating && !isWinner ? 0.3 : undefined;

          return (
            <Box
              key={player.id}
              sx={{
                opacity: animatingOpacity,
                transition: 'opacity 0.5s ease-out',
              }}
            >
              <PlayerOverlayCard
                player={player}
                playerDetails={playerDetails}
                seatNumber={player.seat_number}
                isAnimating={false}
                winner={gameData?.winner}
                showFinalState={showFinalState}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default LiveGameOverlay;